import React, { useState, useEffect } from "react";
import { C, fmt, text } from "../theme";
import { usePermissions } from "../PermissionContext";
import WatcherPanel from "../components/WatcherPanel";
import DocumentSection from "../components/DocumentSection";

import { Card, Btn, Notice, StatusPill as Badge, rowAction } from "../components/ui";

export default function GRNPage() {
  const { me } = usePermissions();
  const [grns, setGRNs] = useState([]);
  const [pos, setPOs] = useState([]);
  const [sel, setSel] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form state
  const [selectedPO, setSelectedPO] = useState(null);
  const [receiveQtys, setReceiveQtys] = useState({});
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }

  const load = () => {
    setLoading(true);
    Promise.all([
      fetch("/api/grns").then(r => r.json()),
      fetch("/api/pos").then(r => r.json()),
    ]).then(([g, p]) => {
      setGRNs(g);
      setPOs(p);
    }).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Only POs that can receive goods
  const receivablePOs = pos.filter(po => ["ISSUED", "PARTIALLY_RECEIVED"].includes(po.status));

  const poDetail = selectedPO ? pos.find(p => p.id === selectedPO) : null;

  const handleSelectPO = (poId) => {
    setSelectedPO(poId);
    const po = pos.find(p => p.id === poId);
    if (po && po.lineItems) {
      const qtys = {};
      po.lineItems.forEach(li => { qtys[li.description] = li.quantity; });
      setReceiveQtys(qtys);
    }
  };

  const handleCreate = async () => {
    if (!selectedPO) { setNotice({ kind: "error", text: "Please select a PO." }); return; }

    const po = pos.find(p => p.id === selectedPO);
    const lineItems = po.lineItems.map(li => ({
      description: li.description,
      receivedQty: receiveQtys[li.description] || 0,
    }));

    if (lineItems.every(li => li.receivedQty === 0)) {
      setNotice({ kind: "error", text: "Please enter received quantities for at least one item." });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/grns", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          poId: selectedPO,
          receivedById: po.requestorId,
          notes: notes.trim() || null,
          lineItems,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // The form stays mounted with its input intact so the user can fix and retry.
        setNotice({ kind: "error", text: "Error: " + (data.error || "Failed to create GRN") });
        return;
      }
      setNotice({ kind: "success", text: "GRN Created: " + data.grnNumber + "\nStatus: " + data.status + "\nPO updated to: " + data.poStatusUpdated });
      setShowForm(false);
      setSelectedPO(null);
      setReceiveQtys({});
      setNotes("");
      load();
    } catch (err) {
      setNotice({ kind: "error", text: "Error: " + err.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.gray500 }}>Loading...</div>;

  // ─── Create Form ───
  if (showForm) {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <Btn variant="outline" onClick={() => { setShowForm(false); setSelectedPO(null); }}>← Cancel</Btn>
          <h1 style={{ fontSize: text.h2, fontWeight: 700, margin: 0 }}>Receive Goods (New GRN)</h1>
        </div>

        <Notice notice={notice} onDismiss={() => setNotice(null)} />

        {/* Step 1: Select PO */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 4 }}>Step 1: Select Purchase Order</div>
          {/* PO lifecycle in plain words (UX-34): "Issued" appears here between
              Approved and the receipt statuses with no visible issue step. */}
          <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 14 }}>
            Select an Issued or Partially Received PO to record goods receipt against.
            A PO becomes <b>Issued</b> automatically at final approval (it is sent to the vendor); receiving all its goods moves it to <b>Fulfilled</b>.
          </div>

          {receivablePOs.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", background: C.warningBg, borderRadius: 8, border: `1px solid ${C.warningLight}` }}>
              <div style={{ fontSize: text.md, fontWeight: 700, color: C.warning }}>No POs Available for Receipt</div>
              <div style={{ fontSize: text.sm, color: C.gray600, marginTop: 4 }}>All POs are either fulfilled or not yet issued.</div>
            </div>
          ) : (
            receivablePOs.map(po => {
              const isSel = selectedPO === po.id;
              return (
                <div key={po.id} onClick={() => handleSelectPO(po.id)} style={{
                  display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", marginBottom: 8,
                  borderRadius: 10, cursor: "pointer",
                  border: isSel ? `2px solid ${C.primary}` : `2px solid ${C.gray200}`,
                  background: isSel ? C.primaryBg : C.white,
                }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                    border: isSel ? `2px solid ${C.primary}` : `2px solid ${C.gray300}`,
                    background: isSel ? C.primary : C.white,
                    display: "flex", alignItems: "center", justifyContent: "center", color: C.white, fontSize: text.sm,
                  }}>{isSel ? "✓" : ""}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: text.lg, fontWeight: 700 }}>{po.poNumber} — {po.vendor}</div>
                    <div style={{ fontSize: text.sm, color: C.gray600 }}>AOR: {po.aor?.aorNumber} • {po.businessUnit}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: text.h3, fontWeight: 700 }}>{fmt(po.total)}</div>
                    <Badge status={po.status} />
                  </div>
                </div>
              );
            })
          )}
        </Card>

        {/* Step 2: Enter quantities */}
        {poDetail && (
          <>
            <Card style={{ marginBottom: 16 }}>
              <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 4 }}>Step 2: Enter Received Quantities</div>
              <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 14 }}>Enter the quantity received for each line item. Partial receipts are supported.</div>

              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
                <thead><tr>{["Description", "Ordered", "Receiving", "Status"].map((h, i) => (
                  <th scope="col" key={i} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
                ))}</tr></thead>
                <tbody>
                  {poDetail.lineItems?.map((li, i) => {
                    const recv = receiveQtys[li.description] || 0;
                    const isFull = recv === li.quantity;
                    const isPartial = recv > 0 && recv < li.quantity;
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                        <td style={{ padding: "10px 12px" }}>{li.description}</td>
                        <td style={{ padding: "10px 12px", fontWeight: 600 }}>{li.quantity}</td>
                        <td style={{ padding: "10px 12px", width: 120 }}>
                          <input type="number" min="0" max={li.quantity} value={recv} aria-label={`Received quantity — ${li.description}`}
                            onChange={e => setReceiveQtys({ ...receiveQtys, [li.description]: Math.min(Number(e.target.value), li.quantity) })}
                            style={{ width: "100%", padding: "6px 8px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.lg, fontWeight: 700, fontFamily: "inherit", textAlign: "center", boxSizing: "border-box" }} />
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          {isFull ? <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: text.xs, fontWeight: 700, background: C.successBg, color: C.success }}>Full</span>
                          : isPartial ? <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: text.xs, fontWeight: 700, background: C.warningBg, color: C.warning }}>Partial</span>
                          : <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: text.xs, fontWeight: 700, background: C.gray100, color: C.gray500 }}>None</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Quick actions */}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <Btn variant="outline" onClick={() => {
                  const qtys = {};
                  poDetail.lineItems.forEach(li => { qtys[li.description] = li.quantity; });
                  setReceiveQtys(qtys);
                }} style={{ padding: "4px 12px", fontSize: text.xs }}>Receive All</Btn>
                <Btn variant="outline" onClick={() => {
                  const qtys = {};
                  poDetail.lineItems.forEach(li => { qtys[li.description] = 0; });
                  setReceiveQtys(qtys);
                }} style={{ padding: "4px 12px", fontSize: text.xs }}>Clear All</Btn>
              </div>
            </Card>

            <Card style={{ marginBottom: 16 }}>
              <label htmlFor="grn-notes" style={{ fontSize: text.sm, fontWeight: 600, color: C.gray700, display: "block", marginBottom: 4 }}>Notes</label>
              <textarea id="grn-notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. All items received in good condition. Service activated." style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.gray300}`, borderRadius: 6, fontSize: text.md, fontFamily: "inherit", minHeight: 60, resize: "vertical", boxSizing: "border-box" }} />
            </Card>

            <div style={{ display: "flex", gap: 10 }}>
              <Btn onClick={handleCreate} disabled={submitting}>{submitting ? "Creating..." : "Create GRN"}</Btn>
              <Btn variant="outline" onClick={() => { setShowForm(false); setSelectedPO(null); }}>Cancel</Btn>
            </div>
          </>
        )}
      </div>
    );
  }

  // ─── Detail View ───
  if (sel) {
    const grn = grns.find(g => g.id === sel);
    if (!grn) return null;

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <Btn variant="outline" onClick={() => setSel(null)} style={{ padding: "4px 10px", fontSize: text.xs }}>← Back</Btn>
          <h1 style={{ fontSize: text.h2, fontWeight: 700, margin: 0 }}>{grn.grnNumber}</h1>
          <Badge status={grn.status} />
        </div>

        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, fontSize: text.md }}>
            <div><span style={{ color: C.gray500 }}>Linked PO:</span> <strong style={{ color: C.primary }}>{grn.po?.poNumber}</strong></div>
            <div><span style={{ color: C.gray500 }}>Vendor:</span> <strong>{grn.po?.vendor}</strong></div>
            <div><span style={{ color: C.gray500 }}>PO Status:</span> <Badge status={grn.po?.status} /></div>
            <div><span style={{ color: C.gray500 }}>Received By:</span> <strong>{grn.receivedBy?.displayName}</strong></div>
            <div><span style={{ color: C.gray500 }}>Date:</span> <strong>{new Date(grn.createdAt).toLocaleDateString()}</strong></div>
          </div>
        </Card>

        {grn.notes && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 8 }}>Notes</div>
            <div style={{ padding: 14, background: C.gray50, borderRadius: 8, fontSize: text.md, lineHeight: 1.6 }}>{grn.notes}</div>
          </Card>
        )}

        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 10 }}>Items Received</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
            <thead><tr>{["Description", "Ordered", "Received", "Variance", "Status"].map((h, i) => (
              <th scope="col" key={i} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
            ))}</tr></thead>
            <tbody>
              {grn.lineItems?.map((li, i) => {
                const isFull = li.receivedQty === li.orderedQty;
                const isPartial = li.receivedQty > 0 && li.receivedQty < li.orderedQty;
                return (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                    <td style={{ padding: "10px 12px" }}>{li.description}</td>
                    <td style={{ padding: "10px 12px" }}>{li.orderedQty}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 700, color: isFull ? C.success : C.warning }}>{li.receivedQty}</td>
                    <td style={{ padding: "10px 12px" }}>
                      {li.variance === 0 ? <span style={{ color: C.success, fontWeight: 600 }}>None</span>
                      : <span style={{ color: C.danger, fontWeight: 600 }}>{li.variance > 0 ? "+" : ""}{li.variance}</span>}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {isFull ? <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: text.xs, fontWeight: 700, background: C.successBg, color: C.success }}>Full</span>
                      : isPartial ? <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: text.xs, fontWeight: 700, background: C.warningBg, color: C.warning }}>Partial</span>
                      : <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: text.xs, fontWeight: 700, background: C.gray100, color: C.gray600 }}>Not Received</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>

        <Card>
          <DocumentSection
            entityType="grn"
            entityId={grn.id}
            documents={grn.documents || []}
            onChange={load}
          />
        </Card>

        <WatcherPanel docType="GRN" docId={grn.id} canManage={!!me && (me.id === grn.receivedById || me.roleName === "Administrator")} />
      </div>
    );
  }

  // ─── List View ───
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: text.h1, fontWeight: 700, color: C.gray1100, margin: 0 }}>Goods Receipt Notes</h1>
          <p style={{ fontSize: text.md, color: C.gray500, margin: "2px 0 0" }}>{grns.length} records</p>
        </div>
        <Btn onClick={() => { setNotice(null); setShowForm(true); }}>+ Receive Goods</Btn>
      </div>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
          <thead><tr>{["GRN No.", "Date", "PO Ref", "Vendor", "Received By", "Status"].map((h, i) => (
            <th scope="col" key={i} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
          ))}</tr></thead>
          <tbody>
            {grns.map(grn => (
              <tr key={grn.id} {...rowAction(() => { setNotice(null); setSel(grn.id); })} style={{ cursor: "pointer", borderBottom: `1px solid ${C.gray100}` }}
                onMouseEnter={e => e.currentTarget.style.background = C.gray50}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "10px 12px", fontWeight: 600, color: C.primary }}>{grn.grnNumber}</td>
                <td style={{ padding: "10px 12px" }}>{new Date(grn.createdAt).toLocaleDateString()}</td>
                <td style={{ padding: "10px 12px", color: C.primary }}>{grn.po?.poNumber}</td>
                <td style={{ padding: "10px 12px" }}>{grn.po?.vendor}</td>
                <td style={{ padding: "10px 12px" }}>{grn.receivedBy?.displayName}</td>
                <td style={{ padding: "10px 12px" }}><Badge status={grn.status} /></td>
              </tr>
            ))}
            {grns.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 30, textAlign: "center", color: C.gray500 }}>No goods receipts yet — record what arrived against an issued PO with + New GRN.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}